import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
// Packages
import SVG from 'react-inlinesvg';
import SideActionBoard from './SideActionBoard';
//API
import { adminAPI, userAPI } from '../api';
// Helpers
import { convertHubSpotLabel, formatDate } from '../helpers';
// Context
import useAlarm from '../context/AlarmContext';
import useData from '../context/DataContext';
import { useModal } from '../context/ModalContext';
// Coponents
import UserInviteForm from './UserInviteForm';
import FormInput from './FormInput';
import CohortMultiSelect from './admin/users/invite/CohortMultiSelect';
import FormLoader from './FormLoader';
import IndependentUserInviteForm from './admin/users/IndependentUserInviteForm';
// Icons
import ChevronRightIcon from '../../../../assets/images/reskin-images/icon--chevrons-right.svg';
import UnlockedIcon from '../../../../assets/images/reskin-images/icon--unlocked.svg';
import LockedIcon from '../../../../assets/images/reskin-images/icon--locked.svg';
import CheckIcon from '../../../../assets/images/reskin-images/icon--check.svg';
import EyeIcon from '../../../../assets/images/reskin-images/icon--eye.svg';
import XIcon from '../../../../assets/images/reskin-images/icon--x.svg';

const activeHubspotProperties = false;

const UserAuditLog = ({ user }) => {
  const [filters, setFilters] = useState({ startDate: '', endDate: '', query: '' });
  const [debouncedQuery, setDebouncedQuery] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(filters.query);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [filters.query]);

  const { data, isLoading } = useQuery(
    ['userAudits', { id: user.id, startDate: filters.startDate, endDate: filters.endDate, query: debouncedQuery }],
    adminAPI.getUserAudits
  );
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [hoveredAuditId, setHoveredAuditId] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    timeoutRef.current = setTimeout(() => {
      setSelectedAudit(null);
      setIsClosing(false);
      timeoutRef.current = null;
    }, 200);
  };

  const handleOpen = (audit) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsClosing(false);
    setSelectedAudit(audit);
  };

  if (isLoading) return <FormLoader />;

  const audits = data?.audits || [];

  return (
    <div className="p-4 pt-0 flex flex-col overflow-hidden" style={{ height: '70vh' }}>
      <div className="flex flex-col gap-2 pb-2 pt-0 border-b border-gray-200 mb-4">
        <p className="flex items-center gap-2 text-gray-500 font-semibold">Filters:</p>
        <div className="flex gap-2 items-end">
          <div className="flex flex-col">
          <span className="text-xs text-gray-500 font-semibold mb-1">Start Date</span>
          <input
            type="date"
            className="border p-2 rounded text-sm"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 font-semibold mb-1">End Date</span>
          <input
            type="date"
            className="border p-2 rounded text-sm"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>
        <div className="flex flex-col flex-grow">
          <span className="text-xs text-gray-500 font-semibold mb-1">Search</span>
          <input
            type="text"
            placeholder="Search Action or Message..."
            className="border p-2 rounded text-sm w-full"
            value={filters.query}
            onChange={(e) => setFilters({ ...filters, query: e.target.value })}
          />
        </div>
          <div className="flex pb-3 ml-auto">
          <button
            className="text-xs text-purple-600 font-semibold hover:underline underline cursor-pointer bg-transparent border-none p-0 appearance-none"
            onClick={() => setFilters({ startDate: '', endDate: '', query: '' })}
          >
            Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-grow overflow-hidden h-full relative">
        <div className="flex flex-col h-full overflow-hidden w-full">
          <div className="overflow-auto flex-grow custom-scrollbar h-full">
            <table className="w-full text-left border-collapse table-fixed">
            <thead className="sticky top-0 bg-gray-100 z-10">
              <tr className="bg-gray-100">
                <th className="border-b p-2 font-semibold w-1/4">Date</th>
                <th className="border-b p-2 font-semibold w-1/4">Action</th>
                <th className="border-b p-2 font-semibold w-1/2">Message</th>
              </tr>
            </thead>
            <tbody>
              {audits.map((audit) => (
                <tr
                  key={audit.id}
                  className={`hover:bg-gray-50 ${selectedAudit?.id === audit.id ? 'bg-purple-200' : ''}`}
                  onMouseEnter={() => setHoveredAuditId(audit.id)}
                  onMouseLeave={() => setHoveredAuditId(null)}
                >
                  <td className="border-b p-2 text-sm whitespace-nowrap relative">
                    <div
                      className={`absolute pin-l pin-t h-full items-center pr-2 ${
                        hoveredAuditId === audit.id ? 'flex' : 'hidden'
                      }`}
                    >
                      <button
                        type="button"
                        className="flex items-center justify-center bg-purple-100 p-2 rounded-lg hover:bg-purple-200"
                        onClick={() => handleOpen(audit)}
                        title="View Details"
                      >
                        <SVG src={EyeIcon} />
                      </button>
                    </div>
                    <span className="truncate block pl-10">{formatDate(audit.created_at, true)}</span>
                  </td>
                  <td className="border-b p-2 text-sm capitalize">{audit.action}</td>
                  <td className="border-b p-2 text-sm relative">{audit.message}</td>
                </tr>
              ))}
              {audits.length === 0 && (
                <tr>
                  <td colSpan="3" className="p-4 text-center text-gray-500">
                    No audit logs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      
      {selectedAudit && (
        <div
          key={selectedAudit.id}
          className={`absolute pin-t pin-r w-3/5 h-full bg-white shadow-lg rounded-lg border border-gray-200 p-4 pt-2 flex flex-col overflow-hidden z-20 ${isClosing ? 'animate-fade-out-right' : 'animate-fade-in-right'}`}
        >
          <div className="flex justify-between items-center mb-2 flex-shrink-0">
            <h3 className="font-bold text-lg">Audit Details</h3>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <SVG src={XIcon} />
            </button>
          </div>
          <div className="overflow-auto flex-grow h-full custom-scrollbar">
            <div>
              <pre className="text-xs bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(selectedAudit.data, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  </div>
  );
};

const DisplayView = ({ user, setOpenSideActionBoard, openSideActionBoard }) => {
  const { setContent } = useModal();

  return (
    user && (
      <>
        <div className="flex justify-between items-center relative pb-4 px-8">
          <button
            className="p-2 flex items-center -ml-2"
            type="button"
            onClick={() => setOpenSideActionBoard()}
          >
            <SVG src={ChevronRightIcon} />
          </button>
          <div className="flex gap-4">
            <button
              className="p-4 text-sm font-bold bg-purple-100 rounded-lg hover:bg-purple-200"
              type="button"
              onClick={() =>
                setContent({
                  modalTitle: `Audit Log for ${user.profile.first_name} ${user.profile.last_name}`,
                  content: <UserAuditLog user={user} />,
                  style: { width: '90%', maxWidth: '1000px' },
                })
              }
            >
              View Audit
            </button>
            <button
              className="p-4 text-sm font-bold bg-purple-100 rounded-lg hover:bg-purple-200"
              type="button"
              onClick={() =>
                setOpenSideActionBoard({ ...openSideActionBoard, action: 'edit' })
              }
            >
              Edit User
            </button>
          </div>
          <span
            style={{ width: 'calc(100% + 6rem)' }}
            className="absolute pin-b pin-r -mx-16 h-px bg-gray flex"
          />
        </div>

      <div className="sidebar-table overflow-y-scroll h-full px-8 py-8">
        <h1>
          {user.profile.first_name} {user.profile.last_name}
        </h1>
        <table className="mt-12">
          <h3 className="mb-4">Account Details</h3>
          <tbody>
            <tr>
              <td>Email</td>
              <td>{user.email ? user.email : '---'}</td>
            </tr>
            <tr>
              <td>Origin</td>
              <td>{user.sign_up_origin ?? '---'}</td>
            </tr>
            <tr>
              <td>User Expiration Date</td>
              <td>
                {user.access_expiration_date
                  ? formatDate(user.access_expiration_date)
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Invitation sent on</td>
              <td>
                {user.user_invite?.created_at
                  ? formatDate(user.user_invite?.created_at)
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Invitation expires on</td>
              <td>
                {user.user_invite?.expires_at
                  ? formatDate(user.user_invite?.expires_at)
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>CRA Internal Contact</td>
              <td>{user.user_invite?.invited_by_email || '---'}</td>
            </tr>
            <tr>
              <td>Company</td>
              <td>
                {user.profile?.company_name || user.profile?.hubspot?.company
                  ? user.profile?.company_name || user.profile?.hubspot?.company
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Cohorts</td>
              <td>
                {user.cohorts && user.cohorts.length > 0
                  ? user.cohorts.map((cohort, index) => (
                      <React.Fragment key={cohort.id}>
                        {cohort.name}
                        {index < user.cohorts.length - 1 && <br />}
                      </React.Fragment>
                    ))
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Alex Expiration Date</td>
              <td>
                {user.alex_expiration_date
                  ? formatDate(user.alex_expiration_date)
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Alex Available</td>
              <td>
                {user.is_alex_available
                  ? 'Yes'
                  : 'No'}
              </td>
            </tr>
            <tr>
              <td>Signed up on</td>
              <td>
                {user.confirmed_at ? formatDate(user.confirmed_at) : '---'}
              </td>
            </tr>
            <tr>
              <td>Last log in</td>
              <td>
                {user.last_sign_in_at
                  ? formatDate(user.last_sign_in_at)
                  : '---'}
              </td>
            </tr>
            <tr>
              <td>Email opt-in</td>
              <td>{user.profile?.opt_in ? 'Yes' : 'No'}</td>
            </tr>
            <tr>
              <td>User Access Type</td>
              <td>{user.user_access_type ? user.user_access_type : '---'}</td>
            </tr>
            <tr>
              <td>ALEX Access Type</td>
              <td>{user.alex_access_type ? user.alex_access_type : '---'}</td>
            </tr>
          </tbody>
        </table>

        {user.hubspot_properties && activeHubspotProperties && (
          <table className="mt-12">
            <h3 className="mb-4">HubSpot Properties</h3>
            <tbody>
              {Object.entries(user.hubspot_properties).map(([key, value]) => (
                <tr key={key}>
                  <td>{convertHubSpotLabel(key)}</td>
                  <td>
                    {key === 'alex_start_date' ? formatDate(value) : value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3 className="mt-12 mb-3">Modules</h3>
        <p className="mb-8 italic">
          Total Percent Completed: {user.percent_complete}%
        </p>
        {user.courses &&
          user.courses.map((course) => {
            const { enrolled_in: enrolledIn, title, behaviors } = course;
            return (
              <>
                <div className="flex items-center mb-4">
                  {enrolledIn ? (
                    <div className="rounded bg-green-lightest p-2 flex items-center justify-center mr-4">
                      <SVG className="svg-green" src={UnlockedIcon} />
                    </div>
                  ) : (
                    <div className="rounded bg-red-lightest p-2 flex items-center justify-center mr-4">
                      <SVG className="svg-red" src={LockedIcon} />
                    </div>
                  )}
                  <p>{title}</p>
                </div>
                {behaviors && (
                  <div
                    style={{ gap: '16px' }}
                    className="ml-4 pl-4 border-l border-gray mb-8 flex flex-col"
                  >
                    {behaviors.map((behavior) => {
                      const { title: behaviorTitle, completed } = behavior;

                      return (
                        <div className="flex items-center">
                          {completed ? (
                            <div className="rounded-full bg-green-lightest flex items-center justify-center p-1 mr-4 -my-2">
                              <SVG className="svg-green" src={CheckIcon} />
                            </div>
                          ) : (
                            <div className="w-8 mr-4" />
                          )}
                          <p>{behaviorTitle}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })}
      </div>
    </>
    )
  );
};

const UserEditForm = ({
  setOpenSideActionBoard,
  openSideActionBoard,
  user,
  cohorts,
}) => {
  const [hasCraEmployee, setHasCraEmployee] = useState(false);
  const [craContactDirty, setCraContactDirty] = useState(false);
  const [inviteFormActive, setInviteFormActive] = useState(!!user?.user_invite);
  const [formData, setFormData] = useState(
    user
      ? {
          id: user.id,
          first_name: user.profile?.first_name,
          last_name: user.profile?.last_name,
          email: user?.email,
          company_name:
            user.profile?.company_name || user.profile?.hubspot?.company,
          alex_expiration_date: user?.alex_expiration_date,
          is_alex_available: user?.is_alex_available,
          source: user.profile?.hubspot?.source,
          source_person: user.profile?.hubspot?.source_person,
          primary_lms_instance: user.profile?.hubspot?.primary_lms_instance,
          cohorts: user?.cohorts?.map(cohort => cohort.id) || [],
          user_invite: user.user_invite
            ? {
                alex_access_type: user.alex_access_type,
                access_type: user.user_access_type || user.profile?.hubspot?.access_type,
                id: user.user_invite?.id,
                expires_at: user.user_invite?.expires_at,
                unlimited_gifting: user?.roles?.includes('unlimited_gifts'),
                invitation_access: user.user_invite?.access_type,
                skip_email: false,
                course_ids: user?.courses,
                valid_for_days: user.user_invite?.valid_for_days,
                expiration_type: user.user_invite?.expiration_type,
                length_type: user.user_invite?.length_type,
                invited_by_id: user.user_invite?.invited_by_id ?? null,
                invited_by_email: user.user_invite?.invited_by_email || '',
                discount_cents:
                  user?.user_invite?.discount_cents === 0
                    ? null
                    : 1000 - user?.user_invite?.discount_cents / 100,
                sign_up_origin: user.sign_up_origin,
                user_access_type: user.user_access_type,
              }
            : {
                alex_access_type: user.alex_access_type,
                access_type: user.user_access_type || user.profile?.hubspot?.access_type,
                sign_up_origin: user.sign_up_origin,
                user_access_type: user.user_access_type,
                skip_email: false,
                course_ids: user?.courses,
              },
        }
      : {}
  );

  useEffect(() => {
    setCraContactDirty(false);
    if (!user?.user_invite) return;
    setFormData((prev) => ({
      ...prev,
      user_invite: {
        ...prev.user_invite,
        invited_by_id: user.user_invite?.invited_by_id ?? null,
        invited_by_email: user.user_invite?.invited_by_email || '',
      },
    }));
  }, [user?.id, user?.user_invite?.invited_by_id, user?.user_invite?.invited_by_email]);

  const handleChange = (name, value, parent = null) => {
    setFormData((prevData) => {
      if (parent) {
        return {
          ...prevData,
          [parent]: {
            ...prevData[parent],
            [name]: value,
          },
        };
      }
      return { ...prevData, [name]: value };
    });
  };

  const queryClient = useQueryClient();

  const { setAlarm } = useAlarm();

  const invalidateRelevantTables = (originType) => {
    switch (originType) {
      case 'Active Users':
        queryClient.invalidateQueries('activeUsers');
        break;
      case 'Unconfirmed Users':
        queryClient.invalidateQueries('unconfirmedUsers');
        break;
      case 'Pending Invites':
        queryClient.invalidateQueries('pendingInvites');
        break;
      default:
        break;
    }

    queryClient.invalidateQueries('allUsers');
    queryClient.invalidateQueries('userDetails');
  };

  const { mutate, isLoading: mutationIsLoading } = useMutation(
    adminAPI.updateUser,
    {
      onSuccess: (data) => {
        if (data.error) {
          setAlarm({ type: 'error', message: data.message });
        } else {
          const originType = openSideActionBoard?.type;
          setOpenSideActionBoard();
          setAlarm({ type: 'success', message: data.message });
          invalidateRelevantTables(originType);
        }
      },
      onError: (error) => {
        setAlarm({ type: 'error', message: error.message });
      },
    }
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    let payload = inviteFormActive
      ? { ...formData }
      : (() => {
          const { user_invite: _ui, ...rest } = formData;
          return rest;
        })();
    if (
      inviteFormActive &&
      formData.user_invite?.id &&
      !craContactDirty &&
      payload.user_invite
    ) {
      const { invited_by_id: _ibid, invited_by_email: _ibe, ...restInvite } =
        payload.user_invite;
      payload = { ...payload, user_invite: restInvite };
    }
    const serializedData = JSON.stringify(payload);
    mutate(serializedData);
  };

  return (
    <>
      <div className="flex justify-between items-center relative pb-4 px-8">
        <button
          className="p-2 flex items-center -ml-2"
          type="button"
          onClick={() => setOpenSideActionBoard()}
        >
          <SVG src={ChevronRightIcon} />
        </button>
        <button
          className="p-4 text-sm font-bold bg-purple-100 rounded-lg hover:bg-purple-200"
          type="button"
          onClick={() =>
            setOpenSideActionBoard({ ...openSideActionBoard, action: 'view' })
          }
        >
          View Details
        </button>
        <span
          style={{ width: 'calc(100% + 6rem)' }}
          className="absolute pin-b pin-r -mx-16 h-px bg-gray flex"
        />
      </div>
      <form
        className="overflow-y-scroll h-full px-8 py-8"
        onSubmit={handleSubmit}
      >
        <h1>
          {user.profile.first_name} {user.profile.last_name}
        </h1>
        <div style={{ gap: '16px' }} className="flex flex-col mt-12">
          <FormInput
            label="Email"
            name="email"
            value={formData.email}
            type="text"
            handleChange={(e) => handleChange('email', e.target.value)}
          />
          <FormInput
            label="First Name"
            name="first_name"
            value={formData.first_name}
            type="text"
            handleChange={(e) => handleChange('first_name', e.target.value)}
          />
          <FormInput
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            type="text"
            handleChange={(e) => handleChange('last_name', e.target.value)}
          />
          <FormInput
            label="Company"
            name="company"
            value={formData.company_name}
            type="text"
            handleChange={(e) => handleChange('company_name', e.target.value)}
          />
          {/* <div> */}
          <CohortMultiSelect
            cohorts={cohorts}
            selectedCohorts={
              formData.cohorts && Array.isArray(formData.cohorts)
                ? cohorts.filter((cohort) => formData.cohorts.includes(cohort.value))
                : []
            }
            onChangeCohortList={(selectedCohorts) =>
              handleChange(
                'cohorts',
                selectedCohorts.map((cohort) => cohort.value)
              )
            }
          />
          {/* </div> */}
          <div className="mt-2">
            <p className="text-sm text-charcoal font-bold mb-2">
              Alex Available
            </p>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_alex_available"
                  value="true"
                  checked={formData.is_alex_available === true}
                  onChange={(e) => handleChange('is_alex_available', e.target.value === 'true')}
                  className="mr-2"
                />
                <span className="text-sm">YES</span>
              </label>
              <label className="flex items-center ml-6">
                <input
                  type="radio"
                  name="is_alex_available"
                  value="false"
                  checked={formData.is_alex_available === false}
                  onChange={(e) => handleChange('is_alex_available', e.target.value === 'true')}
                  className="mr-2"
                />
                <span className="text-sm">NO</span>
              </label>
            </div>
          </div>
          <FormInput
            label="Alex Expiration"
            name="alex_expiration_date"
            value={
              formData.alex_expiration_date
                ? new Date(formData.alex_expiration_date)
                    .toISOString()
                    .split('T')[0]
                : ''
            }
            type="date"
            handleChange={(e) =>
              handleChange('alex_expiration_date', e.target.value)
            }
          />
          <div>
            <p className="text-sm text-charcoal  font-bold mb-4">
              Access Control
            </p>
            <div className="rounded-lg border-2 border-gray p-2 mt-4">
              <div className="flex items-center">
                <input
                  className="mx-4"
                  type="checkbox"
                  checked={hasCraEmployee}
                  onChange={() => setHasCraEmployee(!hasCraEmployee)}
                />
                <p>CRA Employee</p>
              </div>
            </div>
          </div>
        </div>
        <UserInviteForm
          formData={formData}
          courses={user.courses}
          handleChange={handleChange}
          alexAccessTypes={user.alex_access_types}
          hubspotAccessTypes={user.hubspot_access_types}
          cohorts={cohorts}
          application="ALD+ALEX"
          isEditUser={true}
          checkedAllUsers={false}
          inviteFormActive={inviteFormActive}
          setInviteFormActive={setInviteFormActive}
          onCraContactChange={() => setCraContactDirty(true)}
        />

        <button
          className="p-4 my-10  bg-purple text-white font-semibold rounded-lg"
          type="submit"
          data-testid="admin-user-edit-submit"
        >
          Update User
        </button>
      </form>
      {mutationIsLoading && <FormLoader />}
    </>
  );
};

const IndividualUser = ({
  id,
  openSideActionBoard,
  setOpenSideActionBoard,
  cohorts,
}) => {
  const { action } = openSideActionBoard;

  const { data, isLoading } = useQuery(
    ['userDetails', { id }],
    userAPI.getUserDetails
  );

  const { contentData } = useData();

  const modules = contentData?.modules;

  if (isLoading || !modules) {
    return null;
  }

  const { user } = data;

  if (data.user?.courses && modules.length > 0) {
    const sortedCourses = [...data.user.courses].sort((a, b) => {
      const idA = a.id;
      const idB = b.id;
      return (
        modules.findIndex((module) => module.id === idA) -
        modules.findIndex((module) => module.id === idB)
      );
    });
    data.user.courses = sortedCourses;
  }

  return (
    <>
      {action === 'view' && (
        <DisplayView
          user={user}
          openSideActionBoard={openSideActionBoard}
          setOpenSideActionBoard={setOpenSideActionBoard}
        />
      )}
      {action === 'edit' && (
        <UserEditForm
          user={user}
          openSideActionBoard={openSideActionBoard}
          setOpenSideActionBoard={setOpenSideActionBoard}
          cohorts={cohorts}
        />
      )}
    </>
  );
};

const AdminUserSideBar = ({
  openSideActionBoard,
  setOpenSideActionBoard,
  setCheckedActiveUsers,
  setUnconfirmedUsers,
  setPendingInvites,
  checkedAllActiveUsers,
  setCheckedAllActiveUsers,
  checkedAllUnconfirmedUsers,
  setCheckedAllUnconfirmedUsers,
  checkedAllPendingInvites,
  setCheckedAllPendingInvites,
  totalUsers,
  cohorts,
  filter,
  searchQuery,
}) => {
  const { type, ids } = openSideActionBoard;

  const getCheckedAllUsers = () => {
    switch (type) {
      case 'Active Users':
        return checkedAllActiveUsers;
      case 'Unconfirmed Users':
        return checkedAllUnconfirmedUsers;
      case 'Pending Invites':
        return checkedAllPendingInvites;
      default:
        return false;
    }
  };

  const checkedAllUsers = getCheckedAllUsers();

  return (
    <SideActionBoard setOpenSideActionBoard={setOpenSideActionBoard}>
      {(type === 'Active Users' || type === 'Unconfirmed Users') &&
        ids?.length === 1 && ids[0] !== null && typeof ids[0] === 'number' && (
          <IndividualUser
            id={ids[0]}
            openSideActionBoard={openSideActionBoard}
            setOpenSideActionBoard={setOpenSideActionBoard}
            cohorts={cohorts}
          />
        )}

      {type === 'Invite Users' && (
        <>
          <div className="flex justify-between items-center relative pb-4 px-8">
            <button
              className="p-2 flex items-center -ml-2"
              type="button"
              onClick={() => setOpenSideActionBoard()}
            >
              <SVG src={ChevronRightIcon} />
            </button>
            <span
              style={{ width: 'calc(100% + 6rem)' }}
              className="absolute pin-b pin-r -mx-16 h-px bg-gray flex"
            />
          </div>
          <IndependentUserInviteForm
            title="Invite Users"
            setOpenSideActionBoard={setOpenSideActionBoard}
            checkedAllUsers={checkedAllUsers}
            totalUsers={totalUsers}
            cohorts={cohorts}
            filter={filter}
            searchQuery={searchQuery}
          />
        </>
      )}
      {ids?.length >= 1 && ids.some(id => id !== null && typeof id === 'object' && id.hasOwnProperty('id') && id.hasOwnProperty('email')) && (
        <>
          <div className="flex justify-between items-center relative pb-4 px-8">
            <button
              className="p-2 flex items-center -ml-2"
              type="button"
              onClick={() => setOpenSideActionBoard()}
            >
              <SVG src={ChevronRightIcon} />
            </button>
            <span
              style={{ width: 'calc(100% + 6rem)' }}
              className="absolute pin-b pin-r -mx-16 h-px bg-gray flex"
            />
          </div>
          <IndependentUserInviteForm
            items={ids && ids}
            type={type}
            title="Edit Users"
            setOpenSideActionBoard={setOpenSideActionBoard}
            setCheckedActiveUsers={setCheckedActiveUsers}
            setCheckedAllActiveUsers={setCheckedAllActiveUsers}
            setUnconfirmedUsers={setUnconfirmedUsers}
            setCheckedAllUnconfirmedUsers={setCheckedAllUnconfirmedUsers}
            setPendingInvites={setPendingInvites}
            setCheckedAllPendingInvites={setCheckedAllPendingInvites}
            checkedAllUsers={checkedAllUsers}
            totalUsers={totalUsers}
            cohorts={cohorts}
            filter={filter}
            searchQuery={searchQuery}
          />
        </>
      )}
    </SideActionBoard>
  );
};

export default AdminUserSideBar;
